import React, { useState } from "react";
import { Shield, Eye, EyeOff, Mail } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { SESSION_ENDED_MESSAGE } from "../utils/api";
import OTPVerification from "./OTPVerification";
import ForgotPassword from "./ForgotPassword";
import Register from "./Register";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  
  const { loginWithCredentials, verifyOTP, resendOTP, isOTPRequired, sessionEnded, sessionEndedMessage } = useAuth();

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await loginWithCredentials(email, password);

    if (result.success && result.stage === 'otp_required') {
      setOtpData({
        maskedEmail: result.maskedEmail,
        expiresIn: result.expiresIn
      });
    } else if (!result.success) {
      setError(result.error || "Login failed");
    }
    
    setLoading(false);
  };

  const handleOTPVerification = async (otp) => {
    return await verifyOTP(otp);
  };

  const handleResendOTP = async () => {
    return await resendOTP();
  };

  // Show Forgot Password screen
  if (showForgotPassword) {
    return <ForgotPassword onBackToLogin={() => setShowForgotPassword(false)} />;
  }

  // Staff self-registration (docs/modules/ld-selfservice.md)
  if (showRegister) {
    return <Register onBackToLogin={() => setShowRegister(false)} />;
  }

  // Show OTP verification if required
  if (isOTPRequired() && otpData) {
    return (
      <OTPVerification
        onVerify={handleOTPVerification}
        onResend={handleResendOTP}
        maskedEmail={otpData.maskedEmail}
        expiresIn={otpData.expiresIn}
      />
    );
  }

  // COMMENTED OUT - Guest access functionality disabled
  // const handleGuestAccess = async () => {
  //   setLoading(true);
  //   setError("");

  //   const result = await onLogin(null, null, true); // Third parameter indicates guest access

  //   if (!result.success) {
  //     setError(result.error || "Guest access failed");
  //   }
  //   setLoading(false);
  // };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Title */}
        <div className="flex justify-center">
          <img
            src={`${process.env.PUBLIC_URL}/pkf-logo.svg`}
            alt="PKF Logo"
            className="h-12 w-auto"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          PKF Dashboard Portal
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Firm-Wide Analytics & Management Platform
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {sessionEnded && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800">{sessionEndedMessage || SESSION_ENDED_MESSAGE}</p>
            </div>
          )}

          {/* Email Login Form */}
          <form className="space-y-6" onSubmit={handleEmailLogin}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  // text, not email: the browser's native "@" check would block the
                  // service login `system` (users.is_service); the server validates
                  // the format itself (validateEmailLogin) and rejects anything else.
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your email address"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter admin password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  Forgot Your Password?
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <Shield
                    className="h-5 w-5 text-primary-500 group-hover:text-primary-400"
                    aria-hidden="true"
                  />
                </span>
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </div>
          </form>

          {/* Staff self-registration entry */}
          <div className="mt-6 text-center border-t border-gray-100 pt-4">
            <p className="text-sm text-gray-600">
              No account yet?{" "}
              <button
                type="button"
                onClick={() => setShowRegister(true)}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Register with Your Staff Email
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
